/**
 * @copyright Copyright 2025 Epic Systems Corporation
 * @file hook to indicate if an element is overflowing its container
 * @module Epic.AppOrchard.Hooks.UseIsOverflowing
 */

import { useDimensions } from "@chakra-ui/react";
import { useMemo } from "react";

export function useIsOverflowing(
	containerRef: React.RefObject<HTMLElement>,
	contentRef: React.RefObject<HTMLElement>,
	buffer: number = 0,
): boolean {
	const containerDimensions = useDimensions(containerRef, true);
	const contentDimensions = useDimensions(contentRef, true);

	return useMemo(() => {
		const containerBox = containerDimensions?.contentBox;
		const contentBox = contentDimensions?.contentBox;

		// Wait until dimensions are measured
		if (
			!containerRef.current ||
			!contentRef.current ||
			!containerBox ||
			!contentBox ||
			containerBox.height === 0 ||
			contentBox.height === 0
		) {
			return false;
		}

		const { width: containerWidth, height: containerHeight } = containerBox;
		const { width: contentWidth, height: contentHeight } = contentBox;

		return contentHeight - containerHeight > buffer || contentWidth - containerWidth > buffer;
	}, [buffer, containerRef, contentRef, containerDimensions, contentDimensions]);
}
